/**
 * AddHistoricalPcoFlow — the third branch of the Add PCO chooser.
 *
 * The gesture this exists for (client, 2026-07-30): a PM onboarding a job sits
 * down with Josh's PCO Log and types one PCO's WHOLE billing history — three
 * to fourteen rows — in one sitting. Every row becomes a ledger-only
 * submission: a number, the log's literal worked range, an amount, and whether
 * the GC has approved it to bill. No weeks, no PDFs, nothing emailed.
 *
 * Why a grid and not a one-row-at-a-time dialog: a single-row form makes the
 * PM re-answer "which PCO", "what MOD", "what FWA" once per row, and a failure
 * on row 6 of 9 leaves them counting which ones landed. The grid asks the
 * shared questions once, validates the WHOLE thing before a single POST goes
 * out, and — when a POST does fail — halts, locks what saved, and says so.
 *
 * Two destinations, one grid:
 *   - an existing PCO: pre-flight its submissions, prefill row 1 with the
 *     number its history should start at, and detect clashes locally as the
 *     PM types — the taken list appears in the clash error, on the row that
 *     collides, rather than standing above the grid on every visit. Billing
 *     type and funding are NOT editable here — this screen adds history, it
 *     doesn't re-open the PCO's setup.
 *   - a new one: the minimum a PCO needs to exist and read correctly on the
 *     log — number, description, how it bills GOING FORWARD, and one funding
 *     bucket. The bucket is required (client ruling 2026-07-30 #8): without it
 *     the PCO chips "Open", the xlsx parent row renders as NOT SUBMITTED YET
 *     above green history rows, and Approved-CO-to-date under-reports.
 *
 * Saving is SEQUENTIAL and never rolls back. There is no batch endpoint on
 * purpose — a batch relocates partial failure to somewhere the PM can't see
 * it. Rows post in the order they're read, the row that failed keeps its
 * editor and its reason, and the rows above it lock. Nothing is ever retried
 * automatically, and nothing already saved is re-sent.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Check, Trash2, X } from 'lucide-react';
import { normalizeModNumber } from '@coffman-tm/shared';
import { ApiCallError, api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { formatMoneyCents, formatPcoNumber, gcName, gcShort } from '../lib/format';
import { invalidatePcoSubmissions, submissionErrorMessageForCode } from '../lib/useSubmissions';
import { useSettingsPcos } from '../lib/useSettings';
import {
  applySuggestedStart,
  blankHistoricalRow,
  duplicateContentKeys,
  gridHasErrors,
  gridTotalCents,
  historicalSubmissionPayload,
  nextFreeRowNumber,
  nextRowNumber,
  openRenumberCandidate,
  referenceSourceOf,
  renumberTarget,
  suggestStartNumber,
  validateGrid,
  type ExistingSubmissionLite,
  type HistoricalGridRow,
  type HistoricalRowField,
} from '../lib/historicalGrid';
import { ConfirmModal } from './ConfirmModal';
import { Dropdown, SegButton } from './design';
import { HelpTip } from './HelpTip';
import { MoneyInput } from './MoneyInput';
import { useToast } from './ToastProvider';
import {
  BucketCreateFields,
  BucketRemoveButton,
  DATE_RE,
  blankPendingRow,
  bodyForRow,
  parseAmountCents,
  type BucketRow,
} from './pco/BucketRowEditor';
import type {
  PcoBillingType,
  PcoWriteRequest,
  SettingsPco,
  SubmissionSummary,
} from '@coffman-tm/shared';

/** Sentinel option in the PCO select — switches to the create branch. */
const NEW_PCO = '__new__';

type RowSaveState = 'idle' | 'saving' | 'saved' | 'error';

/** # · date range · amount · spacer · Sent/Approved · remove.
 * The header row and each body row are SEPARATE grids sharing this template,
 * so every column must be intrinsic-size-independent: an `auto` column here
 * once collapsed to zero in the header (whose state cell is empty) while
 * holding the Sent/Approved pair in the rows, shearing the Amount column
 * between the two. The pair is ~147px wide; 150 keeps them locked.
 *
 * Date range is FIXED and Amount sits directly after it, left-aligned, with
 * the spare width in a spacer before the state pair (Brandon 2026-08-05: the
 * range "doesn't need that much room", and the amount was hugging
 * Sent/Approved on the far side of the card). */
const GRID_COLUMNS = 'minmax(104px, 118px) 200px 150px 1fr 150px 32px';
const gridRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: GRID_COLUMNS,
  gap: 10,
  alignItems: 'center',
};

export function AddHistoricalPcoFlow({
  onClose,
  onBack,
  onCreated,
}: {
  onClose: () => void;
  /** Back to the chooser. */
  onBack: () => void;
  /**
   * The PCO the history landed on — the same callback the create form uses, so
   * each mount does what it already does (PCO Log: refetch + peek the drawer;
   * side rail: navigate to the PCO).
   */
  onCreated: (pco: SettingsPco) => void | Promise<void>;
}) {
  const { token, me } = useAuth();
  const toast = useToast();
  const pcos = useSettingsPcos();

  // ----- destination -----
  const [pcoChoice, setPcoChoice] = useState('');
  const [newNum, setNewNum] = useState('');
  const [newDesc, setNewDesc] = useState('');
  // No preselection: weekly-rollup vs lump-sum is a real decision and picking
  // wrong misfiles the PCO, so the PM reads both rather than tabbing past one.
  const [goingForward, setGoingForward] = useState<PcoBillingType | null>(null);
  const [funding, setFunding] = useState<BucketRow>(blankPendingRow);
  const [fundingAmountError, setFundingAmountError] = useState<string | null>(null);
  /** Set once the create branch's PCO exists — a retry never creates a second. */
  const [createdPco, setCreatedPco] = useState<SettingsPco | null>(null);

  // ----- pre-flight -----
  const [existing, setExisting] = useState<SubmissionSummary[] | null>(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [renumberBusy, setRenumberBusy] = useState(false);
  const [renumberError, setRenumberError] = useState<string | null>(null);
  const [collisionDismissed, setCollisionDismissed] = useState(false);

  // ----- shared references + grid -----
  const [mod, setMod] = useState('');
  const [fwa, setFwa] = useState('');
  const [rows, setRows] = useState<HistoricalGridRow[]>(() => [blankHistoricalRow('1')]);

  // ----- save -----
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ n: number; of: number } | null>(null);
  const [rowState, setRowState] = useState<Record<string, RowSaveState>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [touched, setTouched] = useState<
    Record<string, Partial<Record<HistoricalRowField, true>>>
  >({});
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Loop-owned state that must never be read through a stale closure: which
  // rows have already landed (they are NEVER re-sent) and how many times each
  // has been attempted — a 409 on attempt 2+ reads very differently from one
  // on attempt 1 (see the network-drop copy in the loop).
  const savedKeys = useRef(new Set<string>());
  const attempts = useRef(new Map<string, number>());

  /**
   * Every PCO this flow has actually written to, most recently written last.
   * A partial save followed by a change of destination makes "what landed"
   * and "what is selected" two different PCOs, and this is the only thing
   * that still knows the first one.
   */
  const landedPcos = useRef(new Map<string, SettingsPco>());
  const noteLanded = (p: SettingsPco) => {
    landedPcos.current.delete(p.id);
    landedPcos.current.set(p.id, p);
  };

  const rangeRefs = useRef(new Map<string, HTMLInputElement | null>());
  const [focusKey, setFocusKey] = useState<string | null>(null);

  // Row keys whose number the PM typed themselves. Those came off the PCO
  // Log and are the numbers the GC knows, so nothing renumbers them — not the
  // pre-flight landing a round-trip after the grid, and not a change of
  // destination. Keyed per ROW on purpose: the claim then clears itself when
  // a grid is replaced, because the fresh row's key was never marked.
  const handNumbered = useRef(new Set<string>());

  // The PCO picker used to be a native <select autoFocus>; Dropdown has no
  // autoFocus prop, so this reproduces the same "focus the first field on
  // mount" behavior imperatively, once, through the forwarded trigger ref.
  const pcoTriggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    pcoTriggerRef.current?.focus();
  }, []);

  const isCreateBranch = pcoChoice === NEW_PCO;
  // Entering create mode unmounts the + New PCO button that was clicked, so
  // focus would drop to <body>; hand it to the field the PM types next.
  const newNumRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isCreateBranch) newNumRef.current?.focus();
  }, [isCreateBranch]);
  const locked = busy || createdPco !== null;
  const chosenPco = useMemo(
    () => (pcos.data ?? []).find((p) => p.id === pcoChoice) ?? null,
    [pcos.data, pcoChoice],
  );
  const parentNumber = isCreateBranch ? newNum : (chosenPco?.pco_number ?? '');
  const parentLabel = formatPcoNumber(parentNumber) || '—';

  // Active PCOs only, numeric order — a paid or void scope takes no new history.
  const selectable = useMemo(
    () =>
      (pcos.data ?? [])
        .filter((p) => p.status !== 'paid' && p.status !== 'void')
        .sort((a, b) => a.pco_number.localeCompare(b.pco_number, undefined, { numeric: true })),
    [pcos.data],
  );
  // Real PCOs only. Creating one is the button beside this list (2026-08-05),
  // not an entry inside it — the sentinel never reaches the menu, so the
  // dropdown's value is blanked while the create branch is active. EXCEPT
  // once the create branch has actually created its PCO: the disabled picker
  // then names the destination that now exists instead of reading
  // "Select a PCO…" over a live record.
  const pcoOptions = useMemo(
    () => [
      { value: '', label: 'Select a PCO…' },
      ...(createdPco
        ? [
            {
              value: NEW_PCO,
              label: `${formatPcoNumber(createdPco.pco_number)} — ${createdPco.description}`,
            },
          ]
        : []),
      ...selectable.map((p) => ({
        value: p.id,
        label: `${formatPcoNumber(p.pco_number)} — ${p.description}`,
      })),
    ],
    [selectable, createdPco],
  );

  const existingLite = useMemo<ExistingSubmissionLite[]>(
    () =>
      isCreateBranch
        ? []
        : (existing ?? []).map((s) => ({
            submission_number: s.submission_number,
            status: s.status,
          })),
    [existing, isCreateBranch],
  );
  const existingNumbers = useMemo(
    () => existingLite.map((s) => s.submission_number),
    [existingLite],
  );

  // Pre-flight the chosen PCO: what's taken, where the history should start,
  // and which package the MOD/FWA prefill comes from. The first two are no
  // longer prose — they are the number sitting in row 1 and the taken list
  // inside the clash error.
  useEffect(() => {
    // Per-row save state is earned against ONE destination and names it —
    // "Saved 1 of 3", a refused row's reason, an attempt count that turns the
    // next 409 into a network-drop warning. None of it may follow the PM to
    // another PCO. What actually LANDED is remembered separately, in
    // `landedPcos`, because that is real and still has to be published.
    setRowState((s) => (Object.keys(s).length === 0 ? s : {}));
    setRowError((s) => (Object.keys(s).length === 0 ? s : {}));
    setSummary(null);
    setShowAllErrors(false);
    attempts.current.clear();
    if (!token || pcoChoice === '' || pcoChoice === NEW_PCO) {
      setExisting(null);
      setPreflightError(null);
      return;
    }
    let cancelled = false;
    setPreflightBusy(true);
    setPreflightError(null);
    api.submissions
      .list(token, pcoChoice)
      .then((res) => {
        if (cancelled) return;
        setExisting(res.submissions);
        const lite = res.submissions.map((s) => ({
          submission_number: s.submission_number,
          status: s.status,
        }));
        setRows((rs) =>
          applySuggestedStart(rs, suggestStartNumber(lite), {
            // Read off the grid as it stands right now, not off the session:
            // a hand-typed number is the log's number and survives the switch.
            numberEdited: rs.some((r) => handNumbered.current.has(r.key)),
            // A row that LANDED belongs to the PCO it landed on. It is never
            // re-sent, so carrying it here would show it as this PCO's and
            // silently drop it from the next save.
            landed: savedKeys.current.size > 0,
          }),
        );
        const source = referenceSourceOf(res.submissions);
        setMod(source?.mod_number ?? '');
        setFwa(source?.hoffman_fwa ?? '');
        setCollisionDismissed(false);
        setRenumberError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setPreflightError(e instanceof Error ? e.message : "Couldn't read this PCO's submissions.");
      })
      .finally(() => {
        if (!cancelled) setPreflightBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, pcoChoice]);

  // A fresh grid when the PM switches to the create branch. Nothing is taken
  // on a PCO that doesn't exist yet, so its rows simply number 1, 2, 3…
  useEffect(() => {
    if (!isCreateBranch) return;
    setRows([blankHistoricalRow('1')]);
    setMod('');
    setFwa('');
  }, [isCreateBranch]);

  useEffect(() => {
    if (!focusKey) return;
    rangeRefs.current.get(focusKey)?.focus();
    setFocusKey(null);
  }, [focusKey, rows]);

  const errors = useMemo(() => validateGrid(rows, existingNumbers), [rows, existingNumbers]);
  const dupes = useMemo(() => duplicateContentKeys(rows), [rows]);
  const totalCents = gridTotalCents(rows);

  const collision = useMemo(
    () => (collisionDismissed ? null : openRenumberCandidate(existingLite, rows)),
    [collisionDismissed, existingLite, rows],
  );
  const collisionTarget = useMemo(
    () => renumberTarget(existingLite, rows),
    [existingLite, rows],
  );

  const savedCount = rows.filter((r) => rowState[r.key] === 'saved').length;
  const unsavedRows = rows.filter((r) => rowState[r.key] !== 'saved');
  const unsavedCount = unsavedRows.filter(
    (r) => r.range.trim() !== '' || r.amountDollars.trim() !== '',
  ).length;
  const dirty =
    unsavedCount > 0 ||
    (isCreateBranch && createdPco === null && (newNum.trim() !== '' || newDesc.trim() !== ''));

  const errorFor = (r: HistoricalGridRow, field: HistoricalRowField): string | null => {
    if (!showAllErrors && !touched[r.key]?.[field]) return null;
    return errors[r.key]?.[field] ?? null;
  };
  const markTouched = (key: string, field: HistoricalRowField) =>
    setTouched((t) => ({ ...t, [key]: { ...(t[key] ?? {}), [field]: true } }));

  /* ---------------- row edits ---------------- */

  const patchRow = (key: string, patch: Partial<HistoricalGridRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const appendAfter = (key: string) => {
    const i = rows.findIndex((r) => r.key === key);
    const from = rows[i];
    if (!from) return;
    // The new row carries the status forward (a run of Sent rows is the normal
    // shape) and takes the next FREE number — mid-grid, "the row above + 1" is
    // the number the row below already holds, and the duplicate would then be
    // flagged on that untouched row instead of this one. MOD/FWA are shared
    // above the grid, so there is nothing else to carry.
    const fresh = blankHistoricalRow(
      nextFreeRowNumber(nextRowNumber(from.number), rows, existingNumbers),
      from.status,
    );
    setRows((rs) => [...rs.slice(0, i + 1), fresh, ...rs.slice(i + 1)]);
    setFocusKey(fresh.key);
  };

  const addRow = () => {
    const last = rows[rows.length - 1];
    const fresh = blankHistoricalRow(
      nextFreeRowNumber(last ? nextRowNumber(last.number) : '1', rows, existingNumbers),
      last?.status ?? 'closed',
    );
    setRows((rs) => [...rs, fresh]);
    setFocusKey(fresh.key);
  };

  const removeRow = (key: string) => {
    const i = rows.findIndex((r) => r.key === key);
    const kept = rows.filter((r) => r.key !== key);
    // Removing the only row leaves a blank one — an empty grid has nothing to
    // type into and reads like the flow broke.
    const next = kept.length > 0 ? kept : [blankHistoricalRow('1')];
    setRows(next);
    // Keep the cursor in the grid: the row that slid into this one's place,
    // else the one above it, else the fresh blank row.
    const landing = next[Math.min(i, next.length - 1)];
    if (landing) setFocusKey(landing.key);
  };

  /* ---------------- the one-click renumber ---------------- */

  const renumberOpenPackage = async () => {
    if (!token || !collision) return;
    const target = (existing ?? []).find(
      (s) => s.submission_number === collision.submission_number && s.status === 'open',
    );
    if (!target) return;
    setRenumberBusy(true);
    setRenumberError(null);
    try {
      await api.submissions.patch(token, target.id, { submission_number: collisionTarget });
      const refreshed = await api.submissions.list(token, pcoChoice);
      setExisting(refreshed.submissions);
      invalidatePcoSubmissions(pcoChoice);
      toast.push({
        variant: 'success',
        title: 'Renumbered',
        body: `The open package is now ${parentLabel}.${collisionTarget}. Your history keeps the log's numbers.`,
        durationMs: 6000,
      });
    } catch (e) {
      // The banner STAYS — a refused renumber must not leave the PM at a dead
      // end, so it re-reads the PCO (so the rows validate against what is
      // really taken now) and names the other way out: change the row numbers
      // instead.
      const code = e instanceof ApiCallError ? e.payload.error : null;
      setRenumberError(
        `${submissionErrorMessageForCode(
          code,
          e instanceof Error ? e.message : 'Could not renumber the open package.',
          gcName(me),
        )} Change the row numbers below instead — a row that clashes will name what is already taken.`,
      );
      try {
        const refreshed = await api.submissions.list(token, pcoChoice);
        setExisting(refreshed.submissions);
      } catch {
        // Validate against what we last read; the banner already carries the
        // reason the refresh didn't happen.
      }
    } finally {
      setRenumberBusy(false);
    }
  };

  /**
   * Everything this flow has already committed on the server. A partial save
   * (or a create that landed before row 1 was refused) is real, so any exit
   * from that state has to tell the rest of the app — otherwise the PCO Log
   * sits there showing the world as it was before the PM started.
   *
   * It reads `landedPcos`, never the current selection: a partial save on 004
   * followed by a switch to 002 would otherwise refetch 002 — the one PCO
   * nothing happened to — and leave 004 stale. Every PCO that received
   * something is invalidated; the handoff goes to the one written to last,
   * which is the one the PM was working on when they stopped.
   */
  const publishIfLanded = async () => {
    const landed = [...landedPcos.current.values()];
    const last = landed[landed.length - 1];
    if (!last) return;
    for (const p of landed) invalidatePcoSubmissions(p.id);
    await onCreated(last);
  };

  /* ---------------- save ---------------- */

  const doSave = async () => {
    if (!token || busy) return;
    setShowAllErrors(true);
    setFormError(null);
    setSummary(null);

    if (pcoChoice === '') {
      setFormError('Pick the PCO this history belongs to.');
      return;
    }
    const mustCreate = isCreateBranch && createdPco === null;
    if (mustCreate) {
      if (!newNum.trim() || !newDesc.trim()) {
        setFormError('PCO number and description are required.');
        return;
      }
      if (goingForward === null) {
        setFormError('Pick this PCO’s billing method.');
        return;
      }
      if (funding.amountDollars.trim() !== '' && !Number.isFinite(Number(funding.amountDollars))) {
        setFormError('The funding amount must be a number.');
        return;
      }
      // Gate on the AMOUNT, not on isBlankNewRow: a row carrying only a MOD or
      // an approved date is "not blank" but saves a $0 bucket, which is exactly
      // the unfunded-looking PCO the required-funding ruling exists to prevent.
      if (parseAmountCents(funding.amountDollars) === 0) {
        setFundingAmountError(
          'Enter the funding amount for this PCO — the amount on its own is a perfectly good Pending bucket.',
        );
        setFormError('This PCO needs a funding amount before its history can be added.');
        return;
      }
      setFundingAmountError(null);
      if (
        funding.approved &&
        funding.approvedDate.trim() &&
        !DATE_RE.test(funding.approvedDate.trim())
      ) {
        setFormError('Approved date must be YYYY-MM-DD.');
        return;
      }
    }

    // NOTHING posts until the whole grid is clean.
    if (gridHasErrors(errors)) {
      setFormError(
        'Fix the highlighted rows first — nothing is saved until the whole grid is clean.',
      );
      return;
    }

    setBusy(true);
    let target = createdPco ?? chosenPco;

    // The PCO has to exist before its history can hang off it. A failure here
    // never runs the grid — the rows stay on screen, untouched.
    if (mustCreate) {
      // Re-narrowed for the type checker — the validation pass above already
      // refused a null choice, and a silent fallback here would misfile the PCO.
      const forwardType = goingForward;
      if (forwardType === null) {
        setFormError('Pick this PCO’s billing method.');
        setBusy(false);
        return;
      }
      let created: SettingsPco;
      try {
        const body: PcoWriteRequest = {
          pco_number: newNum.trim(),
          description: newDesc.trim(),
          amount_proposed_cents: null,
          status: 'open',
          billing_type: forwardType,
          comments: null,
        };
        created = await api.settings.createPco(token, body);
      } catch (e) {
        setFormError(e instanceof Error ? e.message : 'Failed to create the PCO.');
        setBusy(false);
        return;
      }
      setCreatedPco(created);
      noteLanded(created);
      target = created;
      try {
        await api.pcos.createTranche(token, created.id, bodyForRow(funding));
      } catch (te) {
        // The PCO itself exists, so its history still belongs on it — warn and
        // keep going rather than blocking a create that already succeeded.
        toast.push({
          variant: 'warning',
          title: `PCO ${formatPcoNumber(created.pco_number)} created, but the funding didn't save`,
          body: `Add it from "Edit details" on the PCO page. (${
            te instanceof Error ? te.message : 'unknown error'
          })`,
        });
      }
    }

    if (!target) {
      setFormError('That PCO is no longer available. Refresh and try again.');
      setBusy(false);
      return;
    }

    const queue = rows.filter((r) => !savedKeys.current.has(r.key));
    for (let i = 0; i < queue.length; i++) {
      const r = queue[i]!;
      setProgress({ n: i + 1, of: queue.length });
      setRowState((s) => ({ ...s, [r.key]: 'saving' }));
      const attempt = (attempts.current.get(r.key) ?? 0) + 1;
      attempts.current.set(r.key, attempt);
      try {
        await api.submissions.addHistorical(
          token,
          target.id,
          historicalSubmissionPayload(r, { mod, fwa }),
        );
        savedKeys.current.add(r.key);
        noteLanded(target);
        setRowState((s) => ({ ...s, [r.key]: 'saved' }));
        setRowError((s) => {
          const next = { ...s };
          delete next[r.key];
          return next;
        });
      } catch (e) {
        const code = e instanceof ApiCallError ? e.payload.error : null;
        // A 409 on a RETRY is the network-drop shape: the first attempt may
        // have committed on the server and lost the response on the way back.
        // Never call that a duplicate the PM typed.
        const message =
          code === 'number_conflict' && attempt > 1
            ? `${formatPcoNumber(target.pco_number)}.${r.number.trim()} may have saved on the last attempt — check this PCO's submissions before retrying.`
            : submissionErrorMessageForCode(
                code,
                e instanceof Error ? e.message : "This row didn't save.",
                gcName(me),
              );
        setRowState((s) => ({ ...s, [r.key]: 'error' }));
        setRowError((s) => ({ ...s, [r.key]: message }));
        setProgress(null);
        setBusy(false);
        // Whatever landed before the halt is real money on a real PCO, so the
        // rest of the app is told now — but only via the refetch signal. The
        // full `onCreated` handoff closes this dialog on the PCO Log mount,
        // and the PM still has a row to fix; that fires when they leave.
        invalidatePcoSubmissions(target.id);
        const landed = savedKeys.current.size;
        setSummary(
          landed === 0
            ? `Nothing saved. This row was refused — fix it and save again.`
            : `Saved ${landed} of ${rows.length}. The row below was refused and everything after it is still waiting. The ${landed} already saved stay on the PCO — nothing was undone.`,
        );
        return;
      }
    }

    setProgress(null);
    setBusy(false);
    const count = rows.length;
    invalidatePcoSubmissions(target.id);
    toast.push({
      variant: 'success',
      title: 'History added',
      body: `${count} submission${count === 1 ? '' : 's'} added to PCO ${formatPcoNumber(
        target.pco_number,
      )} · ${formatMoneyCents(totalCents)}`,
      durationMs: 6000,
    });
    await onCreated(target);
    onClose();
  };

  /* ---------------- close ---------------- */

  /**
   * Leave the flow. Anything already committed — saved rows, or a PCO created
   * before the first row was refused — is handed off exactly as a clean finish
   * would hand it off, so the log refreshes and lands on the right row instead
   * of showing the world as it was before the PM started.
   */
  const leave = async () => {
    await publishIfLanded();
    onClose();
  };

  const requestClose = () => {
    if (busy) return;
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    void leave();
  };

  /**
   * What the discard confirm has to be honest about: a create branch that got
   * as far as creating the PCO has already written to production even when
   * ZERO rows saved. Saying "nothing has been saved yet" there is a lie the PM
   * would act on.
   */
  const discardBody = (): string => {
    const created = createdPco;
    if (savedCount > 0 && created) {
      return `PCO ${formatPcoNumber(created.pco_number)} and its funding are already created, and ${savedCount} row${savedCount === 1 ? '' : 's'} already saved onto it. Closing undoes none of that — only the rows you haven't saved are discarded.`;
    }
    if (created) {
      return `PCO ${formatPcoNumber(created.pco_number)} and its funding are already created — closing does not remove them. Only the rows are unsaved; add them later from "Add PCO → Historical Data".`;
    }
    if (savedCount > 0) {
      return `The ${savedCount} row${savedCount === 1 ? '' : 's'} already saved stay on PCO ${parentLabel} — closing does not undo them. Only what you haven't saved is discarded.`;
    }
    // Rows landed, then the PM changed destination: `savedCount` counts the
    // grid on screen, which is a different PCO's now. The rows are still real
    // — name no PCO rather than name the wrong one.
    if (landedPcos.current.size > 0) {
      return 'The rows you already saved stay on the PCO they were saved to — closing does not undo them. Only what you haven’t saved is discarded.';
    }
    return 'Nothing has been saved yet, so closing loses everything typed here.';
  };

  // No dep array on purpose: the handler closes over `dirty`, which changes on
  // nearly every keystroke, and a stale one would skip the discard confirm.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy && !confirmDiscard) requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Idle is always just "Save" (2026-08-05). The count lived here to keep a
  // retry honest about how many rows were left, but the counter above the
  // button already prints it and the failure banner already says what landed
  // — three places saying it made the button the noisiest of them. Progress
  // still counts, because during a sequential save it is the only signal.
  const primaryLabel = busy
    ? progress
      ? `Saving ${progress.n} of ${progress.of}…`
      : 'Saving…'
    : 'Save';
  // The same promise in both idle states, for the same reason.
  const primaryTitle =
    savedCount > 0
      ? 'Saves the rows that have not landed yet — nothing already saved is re-sent.'
      : undefined;

  return (
    // The discard confirm is a SIBLING of this overlay, never a child: nested
    // inside it, a click on the confirm's own scrim bubbles into this
    // overlay's onClick and the cancel silently no-ops.
    <>
      <div
        className="pdf-modal-overlay"
        onClick={requestClose}
        role="dialog"
        aria-modal="true"
        aria-label="Add historical billing to a PCO"
      >
        <div
          className="new-pco-modal new-pco-modal--wide pco-form-labels"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void doSave();
            }
          }}
        >
          <div className="pdf-modal-head">
            <div style={{ minWidth: 0 }}>
              <div
                className="pdf-modal-title"
                style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'visible' }}
              >
                Historical Data
                <HelpTip title="How historical rows bill" learnMore="imported-history">
                  What was already billed on this PCO before the portal, straight off the
                  PCO Log. Each row becomes a submission carrying its amount and the
                  log&rsquo;s own date range — no weeks, no PDFs, and nothing is emailed to{' '}
                  {gcName(me)}. The amounts join submitted-to-date immediately; rows left on{' '}
                  <strong>Sent</strong> also sit in Pending until {gcShort(me)} approves them.
                </HelpTip>
              </div>
            </div>
            <button
              type="button"
              className="pdf-modal-close"
              onClick={requestClose}
              disabled={busy}
              aria-label="Close"
            >
              <X size={14} strokeWidth={2} aria-hidden />
            </button>
          </div>

          <div className="new-pco-body">
            {/* ===== Destination ===== */}
            {/* A plain field above the section cards (client mockup ruling
                2026-08-05) — picking the destination is the step that makes
                the cards appear, not a section beside them. */}
            <div className="new-pco-field" style={{ flexShrink: 0 }}>
              <label className="field-label">PCO</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Dropdown
                  ref={pcoTriggerRef}
                  style={{ flex: 1, minWidth: 0 }}
                  options={pcoOptions}
                  value={isCreateBranch ? (createdPco ? NEW_PCO : '') : pcoChoice}
                  onChange={setPcoChoice}
                  disabled={locked}
                />
                {/* The old "+ New PCO…" menu option, promoted out of the list
                    (2026-08-05): creating is a different act from picking,
                    and burying it among 67 real PCOs made it look like one.
                    GONE while create mode is active (Brandon, same day): first
                    it toggled — a second click silently reset the form; then
                    it disabled — a not-allowed cursor on a visible action
                    "feels like something is wrong". A control with no valid
                    action doesn't render. The ways OUT of create mode are
                    picking a real PCO or Back. */}
                {!isCreateBranch && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-compact"
                    style={{ flexShrink: 0 }}
                    disabled={locked}
                    onClick={() => setPcoChoice(NEW_PCO)}
                  >
                    + New PCO
                  </button>
                )}
              </div>
            </div>

            {isCreateBranch && (
              <>
                <div className="pco-card">
                  <div className="pco-card-head">
                    <span className="pco-card-title">PCO details</span>
                  </div>
                  <div className="pco-card-body">
                    <div className="new-pco-row">
                      <div className="new-pco-field">
                        <label className="field-label">PCO #</label>
                        <input
                          ref={newNumRef}
                          className="field-input mono"
                          value={newNum}
                          onChange={(e) => setNewNum(e.target.value)}
                          disabled={locked}
                          placeholder="002"
                        />
                      </div>
                      <div className="new-pco-field">
                        <label className="field-label">Description</label>
                        <input
                          className="field-input"
                          value={newDesc}
                          onChange={(e) => setNewDesc(e.target.value)}
                          disabled={locked}
                          placeholder="C60A03 Rock Hammering Allowance"
                        />
                      </div>
                    </div>

                    <div className="new-pco-field">
                      <span
                        className="field-label"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        Billing method
                        <HelpTip title="T&amp;M Weekly Rollup vs Lump-Sum" learnMore="pcos-and-funding">
                          The history below is history either way — this is how the PCO bills
                          from here on. <strong>T&amp;M Weekly Rollup</strong> means foreman
                          PDFs build its weeks in the portal; <strong>Lump-Sum</strong> means
                          it is priced outside and only tracked on the log.
                        </HelpTip>
                      </span>
                      <div
                        role="group"
                        aria-label="Billing method"
                        style={{ display: 'inline-flex' }}
                      >
                        <SegButton
                          active={goingForward === 'weekly_rollup'}
                          disabled={locked}
                          onClick={() => setGoingForward('weekly_rollup')}
                          position="left"
                          nowrap
                        >
                          T&amp;M Weekly Rollup
                        </SegButton>
                        <SegButton
                          active={goingForward === 'lump_sum'}
                          disabled={locked}
                          onClick={() => setGoingForward('lump_sum')}
                          position="right"
                          nowrap
                        >
                          Lump-Sum
                        </SegButton>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pco-card">
                  <div className="pco-card-head">
                    <span className="pco-card-title">
                      Funding
                      <HelpTip
                        title="Why funding is required here"
                        learnMore="pcos-and-funding"
                      >
                        Required here, unlike the ordinary create form: a PCO carrying
                        billing history but no funding reads as unfunded everywhere the
                        money is totalled. The amount on its own is a perfectly good{' '}
                        <strong>Pending</strong> bucket; if {gcShort(me)} has already
                        executed it, set the Status to <strong>Funded</strong> and take
                        the MOD, FWA and funded date straight off the log.
                      </HelpTip>
                    </span>
                    <BucketRemoveButton
                      index={0}
                      busy={locked}
                      onRemove={() => setFunding(blankPendingRow())}
                    />
                  </div>
                  <div className="pco-card-body">
                    <BucketCreateFields
                      row={funding}
                      busy={locked}
                      showFundedDate
                      onChange={(patch) => {
                        if (patch.amountDollars !== undefined) setFundingAmountError(null);
                        setFunding((f) => ({ ...f, ...patch }));
                      }}
                    />
                    {fundingAmountError && (
                      <p style={rowNote('var(--danger)')} role="alert">
                        {fundingAmountError}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ===== Pre-flight ===== */}
            {/* Only its transient states show. What it FOUND is not a
                paragraph any more: the start number is prefilled into row 1,
                and the taken list rides inside the clash error on the row
                that actually collides. */}
            {!isCreateBranch && pcoChoice !== '' && (preflightBusy || preflightError) && (
              <p
                className="muted"
                style={{
                  fontSize: 12,
                  lineHeight: 1.5,
                  margin: 0,
                  ...(preflightError ? { color: 'var(--danger)' } : null),
                }}
              >
                {preflightError ?? 'Reading this PCO’s submissions…'}
              </p>
            )}

            {/* ===== Collision with an open package ===== */}
            {collision && (
              <div
                role="alert"
                style={{
                  border: '1px solid var(--warning)',
                  background: 'var(--warning-tint)',
                  borderRadius: 6,
                  padding: '9px 11px',
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: 'var(--ink)',
                }}
              >
                <strong style={{ color: 'var(--warning)', fontWeight: 600 }}>
                  {parentLabel}.{collision.submission_number} is already open
                </strong>
                {` — a foreman PDF opened it. Your history keeps the log's own numbers, so move
                  the open package out of its way instead. Only open packages are ever
                  renumbered.`}
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-green btn-compact"
                    disabled={busy || renumberBusy}
                    onClick={() => void renumberOpenPackage()}
                  >
                    {renumberBusy ? 'Renumbering…' : `Renumber the open package to .${collisionTarget}`}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-compact"
                    disabled={busy || renumberBusy}
                    onClick={() => setCollisionDismissed(true)}
                  >
                    Leave it — I&rsquo;ll renumber my rows
                  </button>
                </div>
                {/* A refused renumber must not be a dead end. The banner stays,
                    the PCO has been re-read so the rows validate and this
                    banner reappear against what is really taken now, and this
                    names the other way out. */}
                {renumberError && (
                  <p style={{ ...rowNote('var(--danger)'), marginTop: 8 }} role="alert">
                    {renumberError}
                  </p>
                )}
              </div>
            )}

            {/* ===== Shared references ===== */}
            {pcoChoice !== '' && (
              <div className="pco-card">
                <div className="pco-card-head">
                  <span className="pco-card-title">MOD / FWA</span>
                  <HelpTip title="Entering MOD/FWA once" learnMore="pcos-and-funding">
                    One PCO&rsquo;s history normally bills under a single pair of
                    references, so they are entered once here. If one row genuinely
                    differs, save the grid and correct that row from its own
                    &ldquo;Edit&rdquo; on the PCO afterwards.
                  </HelpTip>
                </div>
                <div className="pco-card-body">
                  <div className="new-pco-row">
                    <div className="new-pco-field">
                      <label className="field-label">MOD #</label>
                      <input
                        className="field-input mono"
                        value={mod}
                        onChange={(e) => setMod(e.target.value)}
                        onBlur={() => {
                          // Reflect the API's storage normalization (zero-padded 3
                          // digits) so the PM sees what will actually save.
                          const normalized = normalizeModNumber(mod) ?? '';
                          if (normalized !== mod) setMod(normalized);
                        }}
                        disabled={busy}
                        placeholder="002"
                      />
                    </div>
                    <div className="new-pco-field">
                      <label className="field-label">FWA #</label>
                      <input
                        className="field-input mono"
                        value={fwa}
                        onChange={(e) => setFwa(e.target.value)}
                        disabled={busy}
                        placeholder="WA 124-002"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ===== The grid ===== */}
            {pcoChoice !== '' && (
              <div className="pco-card">
                <div className="pco-card-head">
                  <span className="pco-card-title">Billed history</span>
                </div>
                {/* .pco-grid scopes the underline field styling — the grid's
                    cells sit directly in grid rows, with no .new-pco-field
                    wrappers to carry it. */}
                <div className="pco-card-body pco-grid">
                  {/* The table's own header, ruled off from the rows it names.
                      Same eyebrow as the card title above, so the block reads as
                      one type system. The Sent/Approved pair is deliberately
                      unlabelled — two words on two buttons say what they do. */}
                  <div className="pco-grid-head" aria-hidden style={gridRow}>
                    <span>#</span>
                    <span>Date range</span>
                    <span>Amount</span>
                    <span />
                    <span />
                    <span />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {rows.map((r, i) => {
                      // A saved row is a record, not a field: it locks to a muted
                      // line so a retry can never re-send it.
                      if (rowState[r.key] === 'saved') {
                        return (
                          <div
                            key={r.key}
                            style={{ ...gridRow, color: 'var(--ink-3)', fontSize: 13, opacity: 0.8 }}
                          >
                            <span
                              className="mono"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                            >
                              <Check size={13} aria-hidden />
                              {parentLabel}.{r.number.trim()}
                            </span>
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {r.range.trim() || '—'}
                            </span>
                            <span className="mono">
                              {formatMoneyCents(parseAmountCents(r.amountDollars))}
                            </span>
                            <span aria-hidden />
                            <span>{r.status === 'approved' ? 'Approved' : 'Sent'}</span>
                            <span style={{ fontSize: 11 }}>saved</span>
                          </div>
                        );
                      }
                      const numberErr = errorFor(r, 'number');
                      const amountErr = errorFor(r, 'amount');
                      const rangeErr = errorFor(r, 'range');
                      const notes = [numberErr, rangeErr, amountErr].filter(Boolean) as string[];
                      return (
                        <div
                          key={r.key}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter') return;
                            // Ctrl/Cmd+Enter is Save — let it bubble to the modal.
                            if (e.ctrlKey || e.metaKey) return;
                            // Enter on a button is that button's own activation.
                            if ((e.target as HTMLElement).tagName === 'BUTTON') return;
                            e.preventDefault();
                            appendAfter(r.key);
                          }}
                        >
                          <div style={gridRow}>
                            <div className="field-input-group" style={hairline(numberErr)}>
                              <span className="field-prefix" aria-hidden>
                                {parentLabel}.
                              </span>
                              <input
                                className="field-input mono"
                                value={r.number}
                                onChange={(e) => {
                                  // This row's number is theirs from here on —
                                  // neither a late pre-flight nor a change of
                                  // destination may rewrite it.
                                  handNumbered.current.add(r.key);
                                  patchRow(r.key, { number: e.target.value });
                                }}
                                onBlur={() => markTouched(r.key, 'number')}
                                disabled={busy}
                                aria-label={`Row ${i + 1} submission number`}
                              />
                            </div>
                            <input
                              ref={(el) => {
                                rangeRefs.current.set(r.key, el);
                              }}
                              className="field-input"
                              style={hairline(rangeErr)}
                              value={r.range}
                              onChange={(e) => patchRow(r.key, { range: e.target.value })}
                              onBlur={() => markTouched(r.key, 'range')}
                              disabled={busy}
                              placeholder="3.21.26 - 4.11.26"
                              aria-label={`Row ${i + 1} date range`}
                            />
                            {/* MoneyInput owns its own caret handling and takes no
                                onBlur, so the wrapper carries it — React's onBlur
                                is focusout, which bubbles. */}
                            <div onBlur={() => markTouched(r.key, 'amount')}>
                              <MoneyInput
                                className="field-input mono"
                                style={hairline(amountErr)}
                                value={r.amountDollars}
                                onChange={(v) => patchRow(r.key, { amountDollars: v })}
                                disabled={busy}
                                placeholder="0.00"
                              />
                            </div>
                            <span aria-hidden />
                            <div
                              role="group"
                              aria-label={`Row ${i + 1} state`}
                              style={{ display: 'inline-flex' }}
                            >
                              <SegButton
                                active={r.status === 'closed'}
                                disabled={busy}
                                onClick={() => patchRow(r.key, { status: 'closed' })}
                                position="left"
                                nowrap
                              >
                                Sent
                              </SegButton>
                              <SegButton
                                active={r.status === 'approved'}
                                disabled={busy}
                                onClick={() => patchRow(r.key, { status: 'approved' })}
                                position="right"
                                nowrap
                              >
                                Approved
                              </SegButton>
                            </div>
                            <button
                              type="button"
                              className="icon-btn danger"
                              aria-label={`Remove row ${i + 1}`}
                              title="Remove row"
                              onClick={() => removeRow(r.key)}
                              disabled={busy}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          {notes.length > 0 && (
                            <p style={rowNote('var(--danger)')}>{notes.join('  ·  ')}</p>
                          )}
                          {rowError[r.key] && (
                            <p style={rowNote('var(--danger)')} role="alert">
                              {rowError[r.key]}
                            </p>
                          )}
                          {notes.length === 0 && dupes.has(r.key) && (
                            <p style={rowNote('var(--ink-3)')}>
                              Same amount and range as another row — fine if the log really says
                              so.
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="pco-card-foot">
                  <button
                    type="button"
                    className="btn btn-ghost btn-compact"
                    onClick={addRow}
                    disabled={busy}
                  >
                    + Add row
                  </button>
                  <div
                    className="mono"
                    style={{
                      fontSize: 12.5,
                      color: 'var(--ink-2)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {rows.length} row{rows.length === 1 ? '' : 's'} · {formatMoneyCents(totalCents)}
                  </div>
                </div>
              </div>
            )}

            {summary && (
              <div
                role="status"
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: 'var(--ink)',
                  border: '1px solid var(--rule-strong, var(--rule))',
                  borderRadius: 4,
                  padding: '8px 10px',
                }}
              >
                {summary}
              </div>
            )}
            {formError && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div>}

            <div className="new-pco-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onBack}
                disabled={busy || createdPco !== null}
                style={{ marginRight: 'auto' }}
              >
                Back
              </button>
              <button type="button" className="btn btn-ghost" onClick={requestClose} disabled={busy}>
                {savedCount > 0 || createdPco !== null ? 'Done — I’ll finish later' : 'Cancel'}
              </button>
              <button
                type="button"
                className="btn btn-cta"
                onClick={() => void doSave()}
                disabled={busy || pcoChoice === ''}
                title={primaryTitle}
              >
                {primaryLabel}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmDiscard}
        title={`Discard ${unsavedCount} unsaved row${unsavedCount === 1 ? '' : 's'}?`}
        body={discardBody()}
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        destructive
        onConfirm={() => {
          setConfirmDiscard(false);
          void leave();
        }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </>
  );
}

/** Red hairline under a cell whose value the server would refuse. */
function hairline(error: string | null): CSSProperties | undefined {
  return error ? { borderBottomColor: 'var(--danger)' } : undefined;
}

/** The 12px line under a row that carries a per-cell reason. */
function rowNote(color: string): CSSProperties {
  return { fontSize: 12, lineHeight: 1.4, margin: '3px 0 0', color };
}
